import { Box, Flex } from 'rebass';
import Head from 'next/head';
import { dynamoose } from '@apuyou/fotokorner-lib';

import Photo from 'components/photo';
import Container from 'components/container';
import CheckoutBar from 'components/checkoutbar';
import Gallery from 'components/gallery';
import PhotoSwipeProvider from 'components/photoswipeprovider';
import { getDomain } from 'lib/domain';
import { getStudioAttribute } from 'lib/studio';
import { getGallery } from 'lib/gallery';
import { getGalleryPhotos, getPhoto, generateUrl } from 'lib/photo';
import Banner from 'components/banner';

function PageLayout({ children, title, bannerState }) {
  return (
    <>
      <Head>
        <title>{title && `${title} | `}fotokorner</title>
      </Head>
      <Banner {...bannerState} />
      <Container>{children}</Container>
    </>
  );
}

export default function GalleryPage({ studio, gallery }) {
  const bannerState = {
    studio,
    banner: gallery.banner,
    title: gallery.title,
    description: gallery.description,
    small: true,
  };

  // Placeholder during load
  if (!gallery) {
    return (
      <PageLayout bannerState={bannerState}>
        <Flex flexDirection={['column', 'row']}>
          {Array(3)
            .fill()
            .map((_, index1) => (
              <Box key={index1} width={[1, 1 / 3]} marginRight={1}>
                {Array(4)
                  .fill()
                  .map((_, index2) => (
                    <Box key={index2} sx={{ marginBottom: 1 }}>
                      <Photo />
                    </Box>
                  ))}
              </Box>
            ))}
        </Flex>
      </PageLayout>
    );
  }

  return (
    <PhotoSwipeProvider gallery={gallery}>
      <PageLayout
        title={`${gallery.title} – ${studio.name}`}
        bannerState={bannerState}
      >
        <Gallery gallery={gallery} />
      </PageLayout>
      <CheckoutBar gallery={gallery} studio={studio} />
    </PhotoSwipeProvider>
  );
}

export async function getServerSideProps({ req, res, query }) {
  dynamoose.aws.sdk.config.update({
    accessKeyId: process.env.STUDIO_AWS_ACCESS_KEY_ID,
    secretAccessKey: process.env.STUDIO_AWS_SECRET_ACCESS_KEY,
    region: process.env.STUDIO_AWS_REGION,
  });

  res.setHeader('Cache-Control', 'public, s-maxage=1, stale-while-revalidate');

  const hostname = req.headers.host;

  const domain = (await getDomain(null, { domain: hostname }, null))?.toJSON();
  const gallery = (
    await getGallery(null, { id: query.gallery }, null)
  )?.toJSON();
  if (!domain || !gallery) {
    return {
      notFound: true,
    };
  }

  const studio = (await getStudioAttribute('studioId')(domain)).toJSON();
  const photos = (await getGalleryPhotos(gallery)).toJSON();

  return {
    props: {
      domain: domain.domain,
      studio: {
        id: studio.id,
        name: studio.name,
        banner: studio.bannerPhotoId
          ? await getPhoto(studio.bannerPhotoId)
          : null,
        avatar: studio.avatarPhotoId
          ? await getPhoto(studio.avatarPhotoId)
          : null,
      },
      gallery: {
        id: gallery.id,
        title: gallery.title,
        description: gallery.description,
        actions: gallery.actions?.map((action) => ({
          type: action.type,
        })),
        banner: gallery.bannerPhotoId
          ? await getPhoto(gallery.bannerPhotoId)
          : null,
        photos: photos.map((photo) => ({
          id: photo.id,
          filename: photo.filename,
          blur: photo.blur,
          width: photo.width,
          height: photo.height,
          url: generateUrl(photo),
        })),
      },
    },
  };
}
